import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import axios from 'axios';
import { toast } from 'react-toastify';
import { initials } from '../../utils/offerHeaderHelpers';
import '../../styles/simpleOfferLayout.css';

function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw || '';
}

const EMPTY_FORM = { id: null, contactId: null, name: '', role: '', email: '', phone: '' };

function ContactKebab({ oc, onEdit, onRemove }) {
  const trigger = (
    <button
      type="button"
      data-testid="contact-menu-trigger"
      className="simple-contacts__kebab-trigger"
      aria-label="Contact actions"
    >
      &#8942;
    </button>
  );

  return (
    <Popup
      trigger={trigger}
      on="click"
      position="bottom right"
      arrow={false}
      closeOnDocumentClick
      contentStyle={{
        width: 160,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(10,13,18,0.12)',
        border: '1px solid #E9EAEB',
        padding: 0,
      }}
    >
      {(close) => (
        <div className="simple-artist-menu">
          <button
            type="button"
            data-testid="menu-contact-edit"
            className="simple-artist-menu__item"
            onClick={() => { close(); onEdit(oc); }}
          >
            Edit
          </button>
          <div className="simple-artist-menu__divider" />
          <button
            type="button"
            data-testid="menu-contact-remove"
            className="simple-artist-menu__item simple-artist-menu__item--danger"
            onClick={() => { close(); onRemove(oc); }}
          >
            Remove
          </button>
        </div>
      )}
    </Popup>
  );
}

ContactKebab.propTypes = {
  oc: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ContactFormRow({ form, onChange, onSave, onCancel, onSearch, onPickExisting, autocompleteEnabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleNameChange = (e) => {
    const value = e.target.value;
    onChange({ ...form, name: value });

    if (!autocompleteEnabled) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      onSearch(value).then((results) => {
        setSuggestions(results || []);
      }).catch(() => {
        setSuggestions([]);
      });
    }, 250);
  };

  const handlePickExisting = (suggestion) => {
    setSuggestions([]);
    onPickExisting(suggestion);
  };

  const nameBlank = !form.name.trim();
  const showSuggestions = autocompleteEnabled && suggestions.length > 0;

  return (
    <div className="simple-contacts__form-row" data-testid="contact-form-row">
      <div className="simple-contacts__form-grid">
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className="simple-offer__input simple-contacts__input"
            placeholder="Full name"
            value={form.name}
            onChange={handleNameChange}
            onBlur={() => {
              setTimeout(() => setSuggestions([]), 150);
            }}
            data-testid="contact-input-name"
          />
          {showSuggestions && (
            <div className="simple-contacts__suggestions">
              {suggestions.map((s) => (
                <div
                  key={s.id}
                  className="simple-contacts__suggestion"
                  data-testid="contact-suggestion"
                  onMouseDown={() => handlePickExisting(s)}
                >
                  <div className="simple-contacts__suggestion-name">{s.name}</div>
                  {s.title_email_preview && (
                    <div className="simple-contacts__suggestion-preview">{s.title_email_preview}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <input
          type="text"
          className="simple-offer__input simple-contacts__input"
          placeholder="e.g. Booking Agent"
          value={form.role}
          onChange={(e) => onChange({ ...form, role: e.target.value })}
          data-testid="contact-input-role"
        />
        <input
          type="email"
          className="simple-offer__input simple-contacts__input"
          placeholder="name@example.com"
          value={form.email}
          onChange={(e) => onChange({ ...form, email: e.target.value })}
          data-testid="contact-input-email"
        />
        <input
          type="tel"
          className="simple-offer__input simple-contacts__input"
          placeholder="(555) 555-5555"
          value={form.phone}
          onChange={(e) => onChange({ ...form, phone: e.target.value })}
          data-testid="contact-input-phone"
        />
        <div className="simple-contacts__form-actions">
          <button
            type="button"
            className="simple-offer__cancel"
            onClick={onCancel}
            data-testid="contact-form-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="simple-offer__save"
            onClick={onSave}
            disabled={nameBlank}
            data-testid="contact-form-save"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

ContactFormRow.propTypes = {
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  onPickExisting: PropTypes.func,
  autocompleteEnabled: PropTypes.bool,
};

ContactFormRow.defaultProps = {
  onSearch: () => Promise.resolve([]),
  onPickExisting: () => {},
  autocompleteEnabled: false,
};

const SimpleContactsTab = ({ ctrl }) => {
  const [form, setForm] = useState(null);

  const canManage = ctrl.currentUser?.can_manage_offer_contacts;
  const offerContacts = ctrl.offerContacts || [];

  const handleAddClick = () => setForm({ ...EMPTY_FORM });

  const handleEdit = (oc) => {
    setForm({
      id: oc.id,
      contactId: oc.contact?.id || null,
      name: oc.contact?.name || '',
      role: oc.contact?.title || oc.contact?.contact_type || '',
      email: oc.contact?.email || '',
      phone: oc.contact?.phone || '',
    });
  };

  const handleRemove = (oc) => {
    const name = oc.contact?.name || 'this contact';
    if (!window.confirm(`Remove ${name}?`)) return;
    axios
      .delete(`/teams/${ctrl.team.id}/offer_contacts/v3/${oc.id}`, {
        headers: { 'X-CSRF-TOKEN': ctrl.csrfToken },
      })
      .then(() => {
        ctrl.setOfferContacts(offerContacts.filter((c) => c.id !== oc.id));
      })
      .catch(() => toast.error('Failed to remove contact. Please try again.'));
  };

  const handleSave = () => {
    if (!form || !form.name.trim()) return;

    const contactAttrs = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      title: form.role.trim(),
      team_id: ctrl.team.id,
    };

    if (form.id) {
      if (form.contactId) contactAttrs.id = form.contactId;
      axios
        .patch(
          `/teams/${ctrl.team.id}/offer_contacts/v3/${form.id}`,
          {
            v3_offer_contact: {
              v3_offer_id: ctrl.offer.id,
              contact_attributes: contactAttrs,
            },
          },
          { headers: { 'X-CSRF-TOKEN': ctrl.csrfToken } },
        )
        .then((res) => {
          ctrl.setOfferContacts(
            offerContacts.map((c) => (c.id === form.id ? res.data : c)),
          );
          setForm(null);
        })
        .catch(() => toast.error('Failed to update contact. Please try again.'));
    } else {
      axios
        .post(
          `/teams/${ctrl.team.id}/offer_contacts/v3`,
          {
            v3_offer_contact: {
              v3_offer_id: ctrl.offer.id,
              contact_attributes: contactAttrs,
            },
          },
          { headers: { 'X-CSRF-TOKEN': ctrl.csrfToken } },
        )
        .then((res) => {
          ctrl.setOfferContacts([...offerContacts, res.data]);
          setForm(null);
        })
        .catch(() => toast.error('Failed to add contact. Please try again.'));
    }
  };

  const searchContacts = (query) => {
    const omitIds = offerContacts.map((oc) => oc.contact?.id).filter(Boolean);
    return axios
      .get(`/teams/${ctrl.team.id}/contacts/autocomplete`, {
        params: { name: query, omit_ids: omitIds },
      })
      .then((res) => res.data);
  };

  const linkExisting = (suggestion) => {
    // Already on the offer (local list is in sync) — nothing to do.
    if (offerContacts.some((oc) => oc.contact?.id === suggestion.id)) {
      setForm(null);
      return;
    }
    axios
      .post(
        `/teams/${ctrl.team.id}/offer_contacts/v3`,
        {
          v3_offer_contact: {
            v3_offer_id: ctrl.offer.id,
            contact_id: suggestion.id,
          },
        },
        { headers: { 'X-CSRF-TOKEN': ctrl.csrfToken } },
      )
      .then((res) => {
        ctrl.setOfferContacts([...offerContacts, res.data]);
        setForm(null);
      })
      .catch((err) => {
        const messages = err?.response?.data;
        const alreadyLinked =
          Array.isArray(messages) && messages.some((m) => /already been taken/i.test(m));
        if (alreadyLinked) {
          // The contact is linked server-side but our local list was stale, so
          // the autocomplete still offered it. Re-sync from the offer instead of
          // reporting a failure for a link that already exists.
          axios
            .get(`/teams/${ctrl.team.id}/offers/v3/${ctrl.offer.id}`)
            .then((res) => {
              ctrl.setOfferContacts(res.data?.v3_offer_contacts || []);
              setForm(null);
            })
            .catch(() => toast.error('Failed to link contact. Please try again.'));
          return;
        }
        toast.error('Failed to link contact. Please try again.');
      });
  };

  const isAddMode = form !== null && form.id === null;

  return (
    <div className="simple-offer-card simple-contacts-card">
      <div className="simple-offer-card__header">
        <div>
          <h3 className="simple-offer-card__title">Contacts</h3>
          <p className="simple-offer-card__subtitle">
            People connected to this offer — agents, managers, and venue staff.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="simple-offer-card__add"
            onClick={handleAddClick}
            data-testid="add-contact-btn"
          >
            + Add contact
          </button>
        )}
      </div>

      {/* Inline form row (shown above table when adding or editing) */}
      {form && (
        <ContactFormRow
          form={form}
          onChange={setForm}
          onSave={handleSave}
          onCancel={() => setForm(null)}
          onSearch={searchContacts}
          onPickExisting={linkExisting}
          autocompleteEnabled={isAddMode}
        />
      )}

      {/* Column header row */}
      {(offerContacts.length > 0 || form) && (
        <div className="simple-contacts__table-header simple-contacts__grid-row">
          <span className="simple-contacts__col-label">Name</span>
          <span className="simple-contacts__col-label">Role</span>
          <span className="simple-contacts__col-label">Email</span>
          <span className="simple-contacts__col-label">Phone</span>
          <span />
        </div>
      )}

      {/* Empty state */}
      {offerContacts.length === 0 && !form && (
        <div className="simple-contacts__empty" data-testid="contacts-empty">
          <div className="simple-contacts__empty-icon" aria-hidden="true">
            <i className="far fa-address-book" />
          </div>
          <h4 className="simple-contacts__empty-title">No contacts yet</h4>
          <p className="simple-contacts__empty-text">
            Add agents, managers, and venue staff connected to this offer so everyone knows who to reach.
          </p>
          {canManage && (
            <button
              type="button"
              className="simple-contacts__empty-action"
              onClick={handleAddClick}
              data-testid="contacts-empty-add"
            >
              + Add contact
            </button>
          )}
        </div>
      )}

      {/* Contact rows */}
      {offerContacts.map((oc) => {
        const contact = oc.contact || {};
        const role = contact.title || contact.contact_type || '';
        return (
          <div
            key={oc.id}
            className="simple-contacts__row simple-contacts__grid-row"
            data-testid="contact-row"
          >
            {/* Name cell: avatar + name */}
            <div className="simple-contacts__name-cell">
              <div className="simple-contacts__avatar" aria-hidden="true">
                {initials(contact.name)}
              </div>
              <span className="simple-contacts__name">{contact.name || '—'}</span>
            </div>

            {/* Role */}
            <span className="simple-contacts__cell simple-contacts__role">
              {role || '—'}
            </span>

            {/* Email */}
            <span className="simple-contacts__cell">
              {contact.email ? (
                <>
                  <i className="far fa-envelope simple-contacts__cell-icon" aria-hidden="true" />
                  {contact.email}
                </>
              ) : (
                '—'
              )}
            </span>

            {/* Phone */}
            <span className="simple-contacts__cell">
              {contact.phone ? (
                <>
                  <i className="far fa-phone simple-contacts__cell-icon" aria-hidden="true" />
                  {formatPhone(contact.phone)}
                </>
              ) : (
                '—'
              )}
            </span>

            {/* Kebab */}
            {canManage ? (
              <ContactKebab oc={oc} onEdit={handleEdit} onRemove={handleRemove} />
            ) : (
              <span />
            )}
          </div>
        );
      })}
    </div>
  );
};

SimpleContactsTab.propTypes = { ctrl: PropTypes.object.isRequired };

export default SimpleContactsTab;
